/**
 * The results table the API returns for a report, and everything the views
 * do with it: formatting cells, laying out a pivot, adding the total row,
 * shaping chart series, colour rules and export.
 *
 * A table is { columns, rows, totals?, comparison?, pivotTotals?, ... }.
 * Each column has a key (the row property), a label and a role:
 *   dimension   a category, such as Account                 (member)
 *   time        a date bucket, such as Month                (member, granularity)
 *   measure     a value, such as Gross profit               (member, format)
 *   comparison  the value for the comparison period         (of, format)
 *   calculation a calculation or formula                    (type, of, format)
 * Rows hold raw values: numbers, ISO dates and codes.
 * Pure functions, no React.
 */
import { formatTimeBucket } from './cubeQuery';
import { formatMeasure } from './valueFormat';

export const TOTAL_ROW = '__total';
export const OTHER_ROW = '__other';

const NUMERIC_ROLES = new Set(['measure', 'comparison', 'calculation', 'change']);
export const isNumericColumn = (c) => NUMERIC_ROLES.has(c?.role);
export const isCategoryColumn = (c) => c?.role === 'dimension' || c?.role === 'time';

/** A cell as text: numbers in their format, dates as their bucket ("Apr 2026"), empty as a dash. */
export function cellText(column, value) {
  if (value === null || value === undefined || value === '') return '—';
  if (isNumericColumn(column)) return formatMeasure(value, column.format);
  if (column.role === 'time') return String(formatTimeBucket(value, column.granularity || null));
  if (column.type === 'time') return String(formatTimeBucket(value, null));
  return String(value);
}

/** Codes that happen to be digits (IDs, references): shown as written, never added up. */
export const IDENTIFIER_LABEL = /(^|[\s_])(id|id[\s_]value|ref|reference|code|number|no)$/i;
const prettify = (key) => String(key).replace(/_/g, ' ').replace(/\b(\w)/g, (c) => c.toUpperCase());

const isNumberLike = (v) => typeof v === 'number' || (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v));

/**
 * A table from plain rows keyed by label (KPI and metric pages): columns
 * whose values are all numbers are values, the rest categories.
 */
export function tableFromRows(rows = [], formats = {}) {
  const keys = rows.length ? Object.keys(rows[0]).filter((k) => k !== TOTAL_ROW) : [];
  const columns = keys.map((key) => {
    const sample = rows.slice(0, 10).map((r) => r[key]).filter((v) => v !== null && v !== undefined && v !== '');
    const numeric = sample.length > 0 && sample.every(isNumberLike) && !IDENTIFIER_LABEL.test(key);
    const label = prettify(key);
    return numeric
      ? { key, label, role: 'measure', format: formats[key] || null }
      : { key, label, role: 'dimension' };
  });
  const numericKeys = columns.filter(isNumericColumn).map((c) => c.key);
  const shaped = rows.map((r) => {
    const out = { ...r };
    for (const k of numericKeys) if (isNumberLike(out[k])) out[k] = Number(out[k]);
    return out;
  });
  return { columns, rows: shaped };
}

/** The rows of a table that are data (not the total row). */
export const dataRows = (table) => (table?.rows || []).filter((r) => !r[TOTAL_ROW]);

/** The table with its total row added at the bottom, when the API worked out totals. */
export function withTotalRow(table) {
  if (!table?.totals || table.rows.some((r) => r[TOTAL_ROW])) return table;
  const first = table.columns.find(isCategoryColumn);
  const row = { ...table.totals, [TOTAL_ROW]: true };
  if (first) row[first.key] = 'Total';
  return { ...table, rows: [...table.rows, row] };
}

/**
 * For tables: after each comparison column, the change from it as a
 * percentage, so "This year, Last year, Change" read side by side.
 */
export function withComparisonChange(table) {
  if (!table?.comparison) return table;
  const columns = [];
  for (const c of table.columns) {
    columns.push(c);
    if (c.role === 'comparison') columns.push({ key: `change:${c.of}`, role: 'change', of: c.of, label: 'Change', format: 'percent' });
  }
  const rows = table.rows.map((r) => {
    const out = { ...r };
    for (const c of table.columns.filter((x) => x.role === 'comparison')) {
      const now = r[c.of];
      const before = r[c.key];
      out[`change:${c.of}`] = typeof now === 'number' && typeof before === 'number' && before !== 0 ? (now - before) / Math.abs(before) : null;
    }
    return out;
  });
  return { ...table, columns, rows };
}

/** Wall mode: keep the first rows and say how many were left out. */
export function compactTable(table, max = 5) {
  const rows = dataRows(table);
  if (rows.length <= max) return { table, hidden: 0 };
  const total = table.rows.find((r) => r[TOTAL_ROW]);
  return { table: { ...table, rows: [...rows.slice(0, max), ...(total ? [total] : [])] }, hidden: rows.length - max };
}

// ---------------------------------------------------------------------------
// Pivot
// ---------------------------------------------------------------------------

/** The column a pivot setting refers to: a dimension or a date (by member), a value, or a calculation. */
const columnFor = (table, member) => table.columns.find((c) => c.key === member)
  || table.columns.find((c) => isCategoryColumn(c) && c.member === member);

/**
 * Lay a table out as a pivot: the row fields down the side, one column per
 * value of the column fields, a Total column and a Total row. Totals of
 * values come from the API (worked out by Cube, so averages and rates are
 * right); without them, sums are added up and rates are left empty.
 */
export function pivotTable(table, pivot = {}) {
  const rowCols = (pivot.rows || []).map((m) => columnFor(table, m)).filter(Boolean);
  const colCols = (pivot.columns || []).map((m) => columnFor(table, m)).filter(Boolean);
  const valueCol = columnFor(table, pivot.measure) || table.columns.find((c) => c.role === 'measure');
  if (!valueCol) return { columns: [], rows: [] };

  const keyOf = (cols, row) => cols.map((c) => (row[OTHER_ROW] && row.__otherOf === c.key ? '\u0000other' : String(row[c.key] ?? ''))).join('\u0001');
  const textOf = (cols, row) => cols.map((c) => cellText(c, row[c.key])).join(' / ');
  const rowLabel = rowCols.map((c) => c.label).join(' / ') || 'Row';

  // The comparison period's value, shown beside each column when the report compares.
  const twin = table.columns.find((c) => c.role === 'comparison' && c.of === valueCol.key) || null;
  const lines = new Map();
  const heads = new Map();
  for (const row of dataRows(table)) {
    const rk = keyOf(rowCols, row);
    const ck = keyOf(colCols, row);
    if (!heads.has(ck)) heads.set(ck, { label: colCols.length ? textOf(colCols, row) : valueCol.label, sort: ck });
    if (!lines.has(rk)) {
      const raw = Object.fromEntries(rowCols.map((c) => [c.key, row[c.key]]));
      lines.set(rk, { label: rowCols.length ? textOf(rowCols, row) : 'All', raw, other: Boolean(row[OTHER_ROW]), cells: new Map(), before: new Map() });
    }
    const add = (map, v) => map.set(ck, typeof v === 'number' ? (map.get(ck) ?? 0) + v : (map.get(ck) ?? null));
    add(lines.get(rk).cells, row[valueCol.key]);
    if (twin) add(lines.get(rk).before, row[twin.key]);
  }
  const heads2 = [...heads.entries()].sort((a, b) => a[1].sort.localeCompare(b[1].sort));

  const fromCube = valueCol.role === 'measure' && table.pivotTotals?.grand;
  const measure = valueCol.key;
  const cubeTotal = (rows, cols) => new Map((rows || []).map((r) => [keyOf(cols, r), r[measure] === null || r[measure] === undefined ? null : Number(r[measure])]));
  const rowTotals = fromCube ? cubeTotal(table.pivotTotals.byRow, rowCols) : null;
  const colTotals = fromCube ? cubeTotal(table.pivotTotals.byColumn, colCols) : null;
  const grand = fromCube ? Number(table.pivotTotals.grand[0]?.[measure] ?? NaN) : NaN;
  // Shares add up; sums and counts add up; rates and averages do not.
  const additive = valueCol.format !== 'percent' || valueCol.type === 'percentOfTotal';

  // A total across the columns, unless the report turns it off.
  const withTotal = heads2.length > 1 && pivot.totalColumn !== false;
  const versus = ` (${table.comparison?.label || 'comparison'})`;
  const valueColumns = heads2.flatMap(([, h], i) => [
    { key: `p${i}`, role: 'measure', label: h.label, format: valueCol.format, source: valueCol.key },
    ...(twin ? [{ key: `q${i}`, role: 'comparison', of: `p${i}`, label: `${h.label}${versus}`, format: valueCol.format }] : []),
  ]);
  const columns = [
    { key: '__row', role: 'dimension', label: rowLabel },
    ...valueColumns,
    ...(withTotal ? [{ key: '__rowTotal', role: 'measure', label: 'Total', format: valueCol.format, total: true }] : []),
    ...(withTotal && twin ? [{ key: '__rowTotalBefore', role: 'comparison', label: `Total${versus}`, format: valueCol.format, total: true }] : []),
  ];

  const sum = (values) => (values.some((v) => typeof v === 'number') ? values.reduce((s, v) => s + (v || 0), 0) : null);
  const rows = [...lines.entries()].map(([rk, line]) => {
    const out = { __row: line.label, __raw: line.raw };
    if (line.other) out[OTHER_ROW] = true;
    heads2.forEach(([ck], i) => {
      // A missing combination is nothing for counts and sums, and unknown for rates.
      out[`p${i}`] = line.cells.has(ck) ? line.cells.get(ck) : (additive ? 0 : null);
      if (twin) out[`q${i}`] = line.before.get(ck) ?? null;
    });
    // The API works out totals for the current period only, so comparison totals are added up where that is right.
    if (twin && withTotal) out.__rowTotalBefore = additive ? sum(heads2.map((_, i) => out[`q${i}`])) : null;
    if (withTotal) {
      const fromTotals = rowTotals?.get(rk);
      out.__rowTotal = fromTotals !== undefined ? fromTotals : (additive ? sum(heads2.map((_, i) => out[`p${i}`])) : null);
    }
    return out;
  });

  if (rows.length > 1) {
    const total = { __row: 'Total', [TOTAL_ROW]: true };
    heads2.forEach(([ck], i) => {
      const fromTotals = rowCols.length ? colTotals?.get(ck) : undefined;
      total[`p${i}`] = fromTotals !== undefined ? fromTotals
        : rowCols.length === 0 ? rows[0][`p${i}`]
          : (heads2.length === 1 && fromCube && Number.isFinite(grand) ? grand : (additive ? sum(rows.map((r) => r[`p${i}`])) : null));
    });
    if (withTotal) total.__rowTotal = Number.isFinite(grand) ? grand : (additive ? sum(rows.map((r) => r.__rowTotal)) : null);
    if (twin) {
      heads2.forEach((_, i) => { total[`q${i}`] = additive ? sum(rows.map((r) => r[`q${i}`])) : null; });
      if (withTotal) total.__rowTotalBefore = additive ? sum(rows.map((r) => r.__rowTotalBefore)) : null;
    }
    rows.push(total);
  }
  return { columns, rows, pivot: true };
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

const MAX_SERIES = 8;

/**
 * What a chart plots: the x axis (a date when there is one, else the first
 * category), and the series. With two categories and one value, each value
 * of the second category is its own series (the largest eight; the rest
 * are added up as "Other"). Otherwise each value is a series, with its
 * comparison twin when there is one. Points keep the rows they came from,
 * so a click can filter or drill.
 */
export function chartModel(table, { preferTime = true } = {}) {
  const rows = dataRows(table);
  const categories = table.columns.filter(isCategoryColumn);
  const values = table.columns.filter((c) => c.role === 'measure' || c.role === 'calculation');
  if (categories.length === 0 || values.length === 0) return null;
  const time = categories.find((c) => c.role === 'time');
  const x = preferTime && time ? time : categories[0];

  if (categories.length >= 2 && values.length === 1) {
    const s = categories.find((c) => c !== x);
    const v = values[0];
    const size = new Map();
    for (const r of rows) {
      const k = cellText(s, r[s.key]);
      size.set(k, (size.get(k) || 0) + Math.abs(Number(r[v.key]) || 0));
    }
    const ranked = [...size.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
    const kept = new Set(ranked.slice(0, MAX_SERIES));
    const names = [...ranked.slice(0, MAX_SERIES), ...(ranked.length > MAX_SERIES ? ['Other'] : [])];
    const points = new Map();
    for (const r of rows) {
      const xLabel = cellText(x, r[x.key]);
      if (!points.has(xLabel)) points.set(xLabel, { __x: xLabel, __xRaw: r[x.key], __rows: {} });
      const p = points.get(xLabel);
      const name = kept.has(cellText(s, r[s.key])) ? cellText(s, r[s.key]) : 'Other';
      const key = `s${names.indexOf(name)}`;
      p[key] = (p[key] || 0) + (Number(r[v.key]) || 0);
      p.__rows[key] = r;
    }
    const data = [...points.values()]
      .sort((a, b) => (x.role === 'time' ? String(a.__xRaw).localeCompare(String(b.__xRaw)) : 0))
      .map((p) => { names.forEach((_, i) => { if (p[`s${i}`] === undefined) p[`s${i}`] = 0; }); return p; });
    return { x, split: true, series: names.map((name, i) => ({ key: `s${i}`, label: name, format: v.format, source: v.key })), data };
  }

  const comparisons = new Map(table.columns.filter((c) => c.role === 'comparison').map((c) => [c.of, c]));
  const series = [];
  values.forEach((v, i) => {
    // `of` is the value a series shows, so a comparison series is drawn like its value.
    series.push({ key: v.key, label: v.label, format: v.format, source: v.key, of: v.key, colorIndex: i });
    const twin = comparisons.get(v.key);
    if (twin) series.push({ key: twin.key, label: twin.label, format: twin.format, source: twin.key, of: v.key, colorIndex: i, comparison: true });
  });
  const data = rows.map((r) => {
    const p = { __x: cellText(x, r[x.key]), __row: r };
    for (const s of series) p[s.key] = typeof r[s.key] === 'number' ? r[s.key] : null;
    return p;
  });
  return { x, split: false, series, data };
}

// ---------------------------------------------------------------------------
// Colour rules
// ---------------------------------------------------------------------------

export const RULE_COLORS = {
  green: { label: 'Green', hex: '#1e8449' },
  amber: { label: 'Amber', hex: '#c27c0e' },
  red: { label: 'Red', hex: '#c0392b' },
  blue: { label: 'Blue', hex: '#2471a3' },
  grey: { label: 'Grey', hex: '#7f8c8d' },
};

export const RULE_OPERATORS = [
  { value: 'gt', label: 'is above' },
  { value: 'gte', label: 'is at least' },
  { value: 'lt', label: 'is below' },
  { value: 'lte', label: 'is at most' },
  { value: 'eq', label: 'equals' },
  { value: 'between', label: 'is between' },
];

/**
 * Rule values are typed as shown: a percent column's "20" means 20%, which
 * the column holds as 0.2.
 */
export const ruleScale = (format) => (format === 'percent' ? 100 : 1);

export function ruleMatches(rule, value, format) {
  if (typeof value !== 'number') return false;
  const v = value * ruleScale(format);
  switch (rule.operator) {
    case 'gt': return v > rule.value;
    case 'gte': return v >= rule.value;
    case 'lt': return v < rule.value;
    case 'lte': return v <= rule.value;
    case 'eq': return Math.abs(v - rule.value) < 1e-9;
    case 'between': return v >= Math.min(rule.value, rule.value2) && v <= Math.max(rule.value, rule.value2);
    default: return false;
  }
}

const mix = (a, b, t) => {
  const p = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const [x, y] = [p(a), p(b)];
  return `#${x.map((c, i) => Math.round(c + (y[i] - c) * t).toString(16).padStart(2, '0')).join('')}`;
};

/** The lowest and highest value of each column that has a colour scale. */
export function scaleRanges(table, rules = []) {
  const out = {};
  for (const rule of rules.filter((r) => r.kind === 'scale')) {
    const keys = table.columns.filter((c) => (c.source || c.key) === rule.column && !c.total).map((c) => c.key);
    const values = dataRows(table).flatMap((r) => keys.map((k) => r[k])).filter((v) => typeof v === 'number');
    if (values.length) out[rule.column] = { min: Math.min(...values), max: Math.max(...values) };
  }
  return out;
}

/**
 * The colour for a value of a column: the first rule it matches, else its
 * colour scale (red to green, or green to red when lower is better), else none.
 */
export function colorFor(rules = [], column, value, ranges = {}) {
  if (!column || typeof value !== 'number' || column.total) return null;
  const key = column.source || column.key;
  const rule = rules.find((r) => r.kind !== 'scale' && r.column === key && ruleMatches(r, value, column.format));
  if (rule) return RULE_COLORS[rule.color]?.hex || null;
  const scale = rules.find((r) => r.kind === 'scale' && r.column === key);
  const range = ranges[key];
  if (!scale || !range) return null;
  let t = range.max === range.min ? 0.5 : (value - range.min) / (range.max - range.min);
  if (scale.higherIsBetter === false) t = 1 - t;
  return t < 0.5 ? mix(RULE_COLORS.red.hex, RULE_COLORS.amber.hex, t * 2) : mix(RULE_COLORS.amber.hex, RULE_COLORS.green.hex, (t - 0.5) * 2);
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/** Rows for CSV and Excel: one property per column label; numbers stay numbers, the rest as shown. */
export function exportRows(table) {
  if (!table?.columns?.length) return [];
  const labels = new Map();
  for (const c of table.columns) {
    let label = c.label;
    for (let n = 2; [...labels.values()].includes(label); n += 1) label = `${c.label} (${n})`;
    labels.set(c.key, label);
  }
  return table.rows.map((r) => Object.fromEntries(table.columns.map((c) => {
    const v = r[c.key];
    if (isNumericColumn(c)) return [labels.get(c.key), typeof v === 'number' ? v : ''];
    return [labels.get(c.key), v === null || v === undefined ? '' : cellText(c, v)];
  })));
}
